import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Star, Users, Waves, Sun, ChevronDown,
  Phone, Mail, Check, X, Menu, ArrowRight, Utensils,
  HomeIcon, Shield, Clock, MessageCircle, TreePine, Car, Wifi,
  ChefHat, Baby, Dumbbell
} from "lucide-react";

// ── Utility ─────────────────────────────────────────────────────────────────
const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const WHATSAPP_NUMBER = "393446752836";
const WHATSAPP_LINK = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=Hello%2C%20I%20would%20like%20information%20about%20Villa%20Milena`;
const WHATSAPP_OFFER_LINK = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=Hello%2C%20I%27d%20like%20to%20request%20a%20personalised%20summer%202026%20offer%20for%20Villa%20Milena.`;
const EMAIL = "info@vmstaysudsardegna.com";

// ── Data ─────────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { label: "The Villa", id: "why" },
  { label: "Stays", id: "stays" },
  { label: "Location", id: "location" },
  { label: "Gallery", id: "gallery" },
  { label: "FAQ", id: "faq" },
];

const FEATURES: { icon: React.ReactElement; title: string; desc: string }[] = [
  {
    icon: <TreePine className="w-6 h-6" />,
    title: "Pineta Is Morus — Gated Residence",
    desc: "Nestled in the exclusive Pineta Is Morus pine forest, with 24/7 private security, coded gate access and a truly peaceful environment.",
  },
  {
    icon: <Waves className="w-6 h-6" />,
    title: "7 Minutes from the Sea",
    desc: "A private condominium beach — for the exclusive use of residents and their guests — is just a 7-minute stroll through the fragrant pine forest.",
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "4 Bedrooms · Up to 8 Guests",
    desc: "Four bedrooms spread across two elegant floors: generous, light-filled spaces designed for families, couples, and close friends travelling together.",
  },
  {
    icon: <HomeIcon className="w-6 h-6" />,
    title: "Fully Equipped Private Home",
    desc: "Reverse osmosis water, Smart TV, dishwasher, washing machine, baby cot and high chair, beach gear — everything you need is already waiting.",
  },
  {
    icon: <ChefHat className="w-6 h-6" />,
    title: "Private Chef Stefano",
    desc: "On request, Chef Stefano brings authentic Sardinian cuisine to your table — from al-fresco show cookings to elegant gourmet dinners and kids' cooking classes.",
  },
  {
    icon: <Star className="w-6 h-6" />,
    title: "Next to Forte Village",
    desc: "The villa borders the world-renowned Forte Village Resort and Forte Arena — Sardinia's premier outdoor concert stage — for culture and entertainment at your doorstep.",
  },
];

// Real villa gallery images from Storeden CDN
const GALLERY_IMAGES = [
  {
    url: "https://egress.storeden.net/gallery/69f46162202628bc4da79b39",
    thumb: "https://egress.storeden.net/gallery/69f46162202628bc4da79b39",
    alt: "Facciata Villa Milena – Villa in affitto a Santa Margherita di Pula",
    span: "col-span-2 row-span-2",
    height: "h-[420px]",
  },
  {
    url: "https://egress.storeden.net/gallery/69f45c362026287017a79d57",
    thumb: "https://egress.storeden.net/gallery/69f45c362026287017a79d57",
    alt: "Veranda arredata di Villa Milena",
    span: "col-span-1",
    height: "h-[200px]",
  },
  {
    url: "https://egress.storeden.net/gallery/69f22192be7ea0d3c0b1d60d",
    thumb: "https://egress.storeden.net/gallery/69f22192be7ea0d3c0b1d60d",
    alt: "Soggiorno luminoso di Villa Milena",
    span: "col-span-1",
    height: "h-[200px]",
  },
  {
    url: "https://egress.storeden.net/gallery/69f21ab2be7ea01a7bb1d726",
    thumb: "https://egress.storeden.net/gallery/69f21ab2be7ea01a7bb1d726",
    alt: "Zona relax solarium – Lettini e patio esterno Villa Milena",
    span: "col-span-1",
    height: "h-[200px]",
  },
  {
    url: "https://egress.storeden.net/gallery/69f21ab22026286871a09b73",
    thumb: "https://egress.storeden.net/gallery/69f21ab22026286871a09b73",
    alt: "Cucina attrezzata di Villa Milena – Kosher friendly",
    span: "col-span-1",
    height: "h-[200px]",
  },
  {
    url: "https://egress.storeden.net/gallery/69f21ab0be7ea01b7bb1d6c0",
    thumb: "https://egress.storeden.net/gallery/69f21ab0be7ea01b7bb1d6c0",
    alt: "Soggiorno luminoso di Villa Milena",
    span: "col-span-1",
    height: "h-[200px]",
  },
  {
    url: "https://egress.storeden.net/gallery/696909ea5fb8e08c4d2683d8",
    thumb: "https://egress.storeden.net/gallery/696909ea5fb8e08c4d2683d8",
    alt: "Cena privata con Chef Stefano – Villa Milena Pula",
    span: "col-span-1",
    height: "h-[200px]",
  },
  {
    url: "https://egress.storeden.net/gallery/696909cd5fb8e02d65268759",
    thumb: "https://egress.storeden.net/gallery/696909cd5fb8e02d65268759",
    alt: "Baia di sabbia fine – Spiaggia condominiale Pineta Is Morus",
    span: "col-span-1",
    height: "h-[200px]",
  },
  {
    url: "https://egress.storeden.net/gallery/69f21ab05fb8e0ba3098b2c3",
    thumb: "https://egress.storeden.net/gallery/69f21ab05fb8e0ba3098b2c3",
    alt: "Camera da letto Villa Milena",
    span: "col-span-1",
    height: "h-[200px]",
  },
  {
    url: "https://egress.storeden.net/gallery/69f21ab32026286571a09a79",
    thumb: "https://egress.storeden.net/gallery/69f21ab32026286571a09a79",
    alt: "Doccia esterna nel giardino privato – Villa Milena",
    span: "col-span-1",
    height: "h-[200px]",
  },
];

const AMENITIES: { icon: React.ReactElement; label: string }[] = [
  { icon: <Wifi className="w-5 h-5" />, label: "Smart TV & Wi-Fi" },
  { icon: <Utensils className="w-5 h-5" />, label: "Fully equipped kitchen" },
  { icon: <Waves className="w-5 h-5" />, label: "Private outdoor shower" },
  { icon: <Baby className="w-5 h-5" />, label: "Baby cot & high chair" },
  { icon: <Sun className="w-5 h-5" />, label: "Sun loungers & beach gear" },
  { icon: <Dumbbell className="w-5 h-5" />, label: "Tennis, football, sports courts" },
  { icon: <TreePine className="w-5 h-5" />, label: "Gated community bar & playground" },
  { icon: <Shield className="w-5 h-5" />, label: "24/7 private security" },
];

const LOCATION_HIGHLIGHTS: { name: string; distance: string; icon: React.ReactElement }[] = [
  { name: "Private condominium beach", distance: "7 min walk", icon: <Waves className="w-5 h-5" /> },
  { name: "Calaverde & Santa Margherita beach", distance: "10 min walk", icon: <Waves className="w-5 h-5" /> },
  { name: "Forte Village & Forte Arena", distance: "Adjacent", icon: <Star className="w-5 h-5" /> },
  { name: "Pula & Nora archaeological site", distance: "~10 min by car", icon: <MapPin className="w-5 h-5" /> },
  { name: "Chia beaches & dunes", distance: "~10 min by car", icon: <Waves className="w-5 h-5" /> },
  { name: "Is Molas Golf Club", distance: "~10 min by car", icon: <TreePine className="w-5 h-5" /> },
  { name: "Cagliari Airport (CAG)", distance: "~45 min by car", icon: <Car className="w-5 h-5" /> },
];

const FAQ_ITEMS = [
  {
    q: "How do I book Villa Milena?",
    a: "Contact us directly on WhatsApp (+39 344 675 2836) or by email at info@vmstaysudsardegna.com with your preferred dates and the size of your party. We'll confirm availability and send a personalised offer within 24 hours — no third-party platforms, direct contact only.",
  },
  {
    q: "What does the weekly rental include?",
    a: "Each stay includes exclusive use of the entire 4-bedroom villa, furnished veranda and garden, outdoor shower, sun loungers, and all amenities (Smart TV, reverse osmosis water, dishwasher, washing machine, beach gear, baby cot). Premium bed linen and towels are included. Mid-stay housekeeping can be arranged on request.",
  },
  {
    q: "Can we arrange a shorter stay or a tailor-made package?",
    a: "Our summer calendar is primarily in weekly stays (Saturday to Saturday), but short stays, monthly long stays, and special occasion packages are available outside peak season. We also offer thematic packages for Hanukkah, Rosh Hashanah, Christmas, and other celebrations — just get in touch.",
  },
  {
    q: "Is the villa kosher-friendly?",
    a: "Yes. Villa Milena is a certified kosher-friendly estate. On request we prepare the kitchen with separate sets of utensils and cookware, and we assist with sourcing certified kosher products. Please mention your requirements at the time of enquiry.",
  },
  {
    q: "Is there a private chef available?",
    a: "Yes. Chef Stefano is available on request for private gourmet dinners, al-fresco show cookings, themed culinary evenings, and kids' cooking classes — all within the villa or on the terrace under the pine trees.",
  },
  {
    q: "What facilities are available in the Pineta Is Morus community?",
    a: "Residents and guests enjoy three private beach accesses, tennis, five-a-side football, basketball, bocce, and a ping-pong table, a children's playground in the shade of the pine forest, and a community bar hosting aperitifs, themed evenings, live music, and family events throughout the summer.",
  },
];

const HOW_TO_BOOK = [
  {
    step: "01",
    title: "Get in Touch",
    desc: "Contact us on WhatsApp or by email with your preferred dates and the size of your party. We respond within 24 hours.",
  },
  {
    step: "02",
    title: "Receive Your Offer",
    desc: "We'll confirm availability and send a personalised proposal — fully tailored to your group, occasion and preferences.",
  },
  {
    step: "03",
    title: "Confirm & Arrive",
    desc: "Sign the booking agreement, pay the deposit, and prepare to experience Sardinia as it should be experienced.",
  },
];

// ── Animation variants ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

// ── Serif helper ─────────────────────────────────────────────────────────────
const serif: React.CSSProperties = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

// ── Header ───────────────────────────────────────────────────────────────────
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/95 shadow-sm" : "bg-transparent"
      }`}
      style={{ backdropFilter: scrolled ? "blur(8px)" : "none" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <button onClick={() => scrollToSection("hero")} className="flex flex-col items-start">
          <span className={`text-xl font-semibold tracking-wider transition-colors ${scrolled ? "text-foreground" : "text-white"}`} style={serif}>
            Villa Milena
          </span>
          <span className={`text-xs tracking-[0.2em] uppercase transition-colors ${scrolled ? "text-muted-foreground" : "text-white/70"}`}>
            VM Stay · Sud Sardegna
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`text-sm tracking-wide transition-colors hover:opacity-70 ${scrolled ? "text-foreground" : "text-white"}`}
            >
              {item.label}
            </button>
          ))}
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-all hover:opacity-90"
            style={{ backgroundColor: "var(--primary)" }}
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
        </nav>

        <button
          className={`md:hidden ${scrolled ? "text-foreground" : "text-white"}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-border overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { scrollToSection(item.id); setMenuOpen(false); }}
                  className="text-foreground text-sm text-left tracking-wide hover:text-primary transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 justify-center px-5 py-3 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: "var(--primary)" }}
              >
                <MessageCircle className="w-4 h-4" />
                Contact on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://skyagent-artifacts.skywork.ai/image/7c89fd7c-26d0-4a2d-932d-0a97afd0b24e/8a9e3a70-40f5-4573-8806-9e7b8b540a16/prod_agent_7c89fd7c-26d0-4a2d-932d-0a97afd0b24e/hero_v3_20260508075136_1.png"
          alt="Villa Milena – Santa Margherita di Pula, Sud Sardegna"
          className="w-full h-full object-cover"
          style={{
            objectPosition: "center 45%",
          }}
        />
        {/* Overlay: transparent on top to preserve blue sky, darker only at bottom for text legibility */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,20,40,0.08) 0%, rgba(10,20,40,0.12) 40%, rgba(10,20,40,0.62) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-white/75 text-xs tracking-[0.35em] uppercase mb-6"
        >
          Pineta Is Morus · Santa Margherita di Pula · South Sardinia
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.38, ease: [0.22, 1, 0.36, 1] }}
          className="text-white mb-6"
          style={{
            ...serif,
            fontSize: "clamp(3rem, 7.5vw, 5.8rem)",
            fontWeight: 500,
            lineHeight: 1.08,
            letterSpacing: "-0.01em",
          }}
        >
          Your Summer<br />
          <em style={{ fontStyle: "italic", fontWeight: 400 }}>in Sardinia</em>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.56 }}
          className="text-white/85 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed"
        >
          A private luxury villa for families and groups of up to 8 guests — set in 
          an exclusive pine forest reserve, 7 minutes from the sea, steps from 
          Forte Village.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.72 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2.5 px-8 py-4 rounded-full text-white font-medium text-sm tracking-wide transition-all hover:opacity-90 hover:scale-105"
            style={{ backgroundColor: "var(--primary)", boxShadow: "0 4px 24px rgba(0,0,0,0.3)" }}
          >
            <MessageCircle className="w-5 h-5" />
            Contact us on WhatsApp
          </a>
          <button
            onClick={() => scrollToSection("stays")}
            className="flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-medium text-sm tracking-wide border border-white/35 hover:bg-white/10 transition-all"
          >
            View Summer 2026 Stays
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="flex flex-wrap gap-3 justify-center mt-12"
        >
          {["4 Bedrooms · Up to 8 guests", "7 min from the beach", "Next to Forte Village", "Kosher-friendly on request", "Chef available on request"].map((badge) => (
            <span
              key={badge}
              className="px-4 py-1.5 rounded-full text-white/90 text-xs tracking-wide"
              style={{ background: "rgba(255,255,255,0.11)", border: "1px solid rgba(255,255,255,0.18)" }}
            >
              {badge}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/55 cursor-pointer"
        onClick={() => scrollToSection("why")}
      >
        <span className="text-xs tracking-[0.2em] uppercase">Discover</span>
        <ChevronDown className="w-5 h-5 animate-bounce" />
      </motion.div>
    </section>
  );
}

// ── Why Choose ───────────────────────────────────────────────────────────────
function WhySection() {
  return (
    <section id="why" className="py-28 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-20"
        >
          <p className="text-sm tracking-[0.25em] uppercase mb-4" style={{ color: "var(--primary)" }}>
            Why Villa Milena
          </p>
          <h2
            className="text-foreground mb-6"
            style={{ ...serif, fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, lineHeight: 1.15 }}
          >
            A villa designed for those<br />
            who appreciate the <em style={{ fontStyle: "italic" }}>exceptional</em>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
            Enchantingly nestled in the heart of Southern Sardinia, Villa Milena is a pure and elegant jewel 
            adorned with the light and scents of the Mediterranean. Here, every detail is designed to welcome 
            your happiest days.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {FEATURES.map((f) => (
            <motion.div
              key={f.title}
              variants={fadeUp}
              className="group p-8 rounded-2xl border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
              style={{ background: "var(--card)" }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-all group-hover:scale-110"
                style={{
                  background: "color-mix(in srgb, var(--primary) 10%, transparent)",
                  color: "var(--primary)",
                }}
              >
                {f.icon}
              </div>
              <h3 className="text-foreground mb-3 text-xl" style={{ ...serif, fontWeight: 600 }}>
                {f.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed text-sm">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Amenities strip */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-16 p-8 rounded-3xl border border-border"
          style={{ background: "oklch(0.97 0.008 75)" }}
        >
          <p className="text-center text-sm tracking-[0.2em] uppercase text-muted-foreground mb-8">
            Amenities & Services
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {AMENITIES.map((a) => (
              <div key={a.label} className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "color-mix(in srgb, var(--secondary) 10%, transparent)", color: "var(--secondary)" }}
                >
                  {a.icon}
                </div>
                <span className="text-foreground text-sm">{a.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* 2-image strip */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-3 gap-3 rounded-3xl overflow-hidden"
          style={{ height: "300px" }}
        >
          <div className="col-span-2 overflow-hidden">
            <img
              src="https://egress.storeden.net/gallery/69f21ab2be7ea05a89b1d658"
              alt="Soggiorno di Villa Milena – elegante e luminoso"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
          <div className="col-span-1 overflow-hidden">
            <img
              src="https://egress.storeden.net/gallery/69f21ab2be7ea01a7bb1d726"
              alt="Zona relax esterna – lettini e solarium Villa Milena"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Summer Stays ─────────────────────────────────────────────────────────────
function StaysSection() {
  return (
    <section
      id="stays"
      className="py-28 lg:py-36"
      style={{ background: "oklch(0.96 0.015 75)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-16"
        >
          <p className="text-sm tracking-[0.25em] uppercase mb-4" style={{ color: "var(--primary)" }}>
            Summer 2026
          </p>
          <h2
            className="text-foreground mb-6"
            style={{ ...serif, fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, lineHeight: 1.15 }}
          >
            Weekly Stays
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-lg leading-relaxed">
            Our summer calendar runs week by week — full villa, exclusive use, for up to 8 guests.
            Contact us directly for availability and current pricing.
          </p>
        </motion.div>

        {/* Package cards */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6 mb-14"
        >
          {[
            {
              period: "Early Season",
              dates: "June – early July 2026",
              highlights: ["More availability", "Ideal for longer stays", "Beach just waking up"],
              cta: "Check June dates",
              color: "var(--secondary)",
            },
            {
              period: "High Season",
              dates: "July – August 2026",
              highlights: ["Peak Sardinian summer", "Forte Arena concerts", "Community events & aperitifs"],
              cta: "Request July–August offer",
              color: "var(--primary)",
              featured: true,
            },
            {
              period: "Late Season",
              dates: "Late August – September 2026",
              highlights: ["Sea still warm", "Fewer crowds", "Ideal for September stays"],
              cta: "Check September dates",
              color: "var(--secondary)",
            },
          ].map((pkg) => (
            <motion.div
              key={pkg.period}
              variants={fadeUp}
              className={`rounded-3xl p-8 flex flex-col ${
                pkg.featured
                  ? "text-white"
                  : "bg-white border border-border text-foreground"
              }`}
              style={pkg.featured ? { background: "var(--primary)" } : {}}
            >
              {pkg.featured && (
                <span className="text-xs tracking-[0.2em] uppercase mb-4 text-white/70">Most Popular</span>
              )}
              <h3
                className={`text-2xl mb-1 ${pkg.featured ? "text-white" : "text-foreground"}`}
                style={{ ...serif, fontWeight: 600 }}
              >
                {pkg.period}
              </h3>
              <p className={`text-sm mb-6 ${pkg.featured ? "text-white/70" : "text-muted-foreground"}`}>
                {pkg.dates}
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {pkg.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2.5 text-sm">
                    <Check
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: pkg.featured ? "rgba(255,255,255,0.8)" : "var(--primary)" }}
                    />
                    <span className={pkg.featured ? "text-white/85" : "text-foreground"}>{h}</span>
                  </li>
                ))}
              </ul>
              <a
                href={WHATSAPP_OFFER_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-all hover:opacity-90 ${
                  pkg.featured ? "bg-white text-foreground" : "border border-primary text-primary hover:bg-primary hover:text-white"
                }`}
              >
                {pkg.cta}
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </motion.div>
          ))}
        </motion.div>

        {/* Long stay */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-3xl p-10 border border-border flex flex-col md:flex-row gap-8 items-center"
        >
          <div className="flex-1">
            <p className="text-xs tracking-[0.25em] uppercase mb-3" style={{ color: "var(--secondary)" }}>
              Long Stay
            </p>
            <h3 className="text-foreground text-2xl mb-3" style={{ ...serif, fontWeight: 600 }}>
              Stay Longer. Live Sardinia.
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Monthly long-stay packages are available for families, remote workers, and guests 
              who wish to experience South Sardinia at a slower, deeper pace. June and September 
              are particularly suited — cooler, quieter, and unforgettable.
            </p>
          </div>
          <a
            href={WHATSAPP_OFFER_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-sm text-white transition-all hover:opacity-90"
            style={{ backgroundColor: "var(--secondary)" }}
          >
            Request a long-stay offer
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

// ── Tailor-Made ──────────────────────────────────────────────────────────────
function TailorMadeSection() {
  return (
    <section className="py-28 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden aspect-[4/5]">
              <img
                src="https://egress.storeden.net/gallery/696909ea5fb8e05d86268335"
                alt="Chef Stefano all'opera – cena privata a Villa Milena"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div
              className="absolute -bottom-6 -right-6 bg-white rounded-2xl px-6 py-5 shadow-xl border border-border hidden md:block"
              style={{ maxWidth: "220px" }}
            >
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1">Available on request</p>
              <p className="text-xl font-semibold text-foreground" style={serif}>
                Chef Stefano
              </p>
              <p className="text-xs text-muted-foreground mt-1">Private gourmet dining</p>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.25em] uppercase mb-5" style={{ color: "var(--primary)" }}>
              Tailor-Made Stays
            </p>
            <h2
              className="text-foreground mb-6"
              style={{ ...serif, fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 500, lineHeight: 1.2 }}
            >
              A stay shaped around<br />
              <em style={{ fontStyle: "italic" }}>your</em> traditions
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6 text-lg">
              Villa Milena is more than a villa: it's a place where the home you find opens up to 
              embrace who you are — your habits, your traditions, your way of celebrating. Our weekly 
              packages are designed to make magic of the occasions you hold dearest.
            </p>
            <ul className="space-y-3 mb-10">
              {[
                "Hanukkah, Rosh Hashanah & Passover packages",
                "Christmas, Easter & New Year celebrations",
                "Private chef Stefano for gourmet dinners",
                "Kids' cooking classes & family activities",
                "Yoga retreats, digital detox, wellness stays",
                "Grocery stocking, welcome hamper & transfers",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-foreground">
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "var(--primary)" }} />
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <a
              href={WHATSAPP_OFFER_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-sm text-white transition-all hover:opacity-90"
              style={{ backgroundColor: "var(--primary)" }}
            >
              Request a tailor-made stay
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── Kosher ───────────────────────────────────────────────────────────────────
function KosherSection() {
  return (
    <section
      className="py-24 lg:py-28"
      style={{ background: "color-mix(in srgb, var(--secondary) 6%, white)" }}
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-8"
            style={{ background: "color-mix(in srgb, var(--secondary) 12%, transparent)" }}
          >
            <Utensils className="w-7 h-7" style={{ color: "var(--secondary)" }} />
          </div>
          <p className="text-sm tracking-[0.25em] uppercase mb-4" style={{ color: "var(--secondary)" }}>
            On Request
          </p>
          <h2
            className="text-foreground mb-6"
            style={{ ...serif, fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, lineHeight: 1.2 }}
          >
            A Kosher-Friendly Estate
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-6 max-w-2xl mx-auto text-lg">
            Villa Milena warmly welcomes guests from all cultures and traditions of the world. 
            On request, we prepare the kitchen with separate sets of utensils and cookware, and assist 
            with sourcing certified kosher products from trusted local suppliers.
          </p>
          <p className="text-muted-foreground text-sm max-w-xl mx-auto mb-10">
            We also offer dedicated packages for Hanukkah, Rosh Hashanah, Passover, and other occasions — 
            with Chef Stefano able to interpret every celebration through an authentic Sardinian lens.
            Please mention your requirements at the time of enquiry.
          </p>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-sm text-white transition-all hover:opacity-90"
            style={{ backgroundColor: "var(--secondary)" }}
          >
            <MessageCircle className="w-4 h-4" />
            Enquire about kosher arrangements
          </a>
        </motion.div>
      </div>
    </section>
  );
}

// ── Location ─────────────────────────────────────────────────────────────────
function LocationSection() {
  return (
    <section id="location" className="py-28 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.25em] uppercase mb-5" style={{ color: "var(--primary)" }}>
              Location
            </p>
            <h2
              className="text-foreground mb-4"
              style={{ ...serif, fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 500, lineHeight: 1.2 }}
            >
              South Sardinia at<br />its most <em style={{ fontStyle: "italic" }}>beautiful</em>
            </h2>
            <p className="text-muted-foreground text-sm mb-2">
              <span className="font-medium text-foreground">Località Santa Margherita di Pula</span>, 
              within the Comunione Pineta Is Morus, bordering the Calaverde Tourist Port. 
              SS 195 km 37.88, 09050 Pula (CA)
            </p>
            <p className="text-muted-foreground leading-relaxed mb-10 text-base">
              Every day is an opportunity to experience Sardinia at its best — combining relaxation, 
              adventure, and culture in an environment of absolute excellence.
            </p>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-3"
            >
              {LOCATION_HIGHLIGHTS.map((loc) => (
                <motion.div
                  key={loc.name}
                  variants={fadeUp}
                  className="flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary/30 transition-all hover:shadow-sm"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background: "color-mix(in srgb, var(--primary) 10%, transparent)",
                      color: "var(--primary)",
                    }}
                  >
                    {loc.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground text-sm font-medium">{loc.name}</p>
                  </div>
                  <span className="text-muted-foreground text-sm flex-shrink-0 font-medium">{loc.distance}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="space-y-3"
          >
            <div className="rounded-3xl overflow-hidden" style={{ height: "340px" }}>
              <img
                src="https://egress.storeden.net/gallery/696909cd00f2201b645eb018"
                alt="Spiaggia di Cala Verde – Pineta Is Morus, Santa Margherita di Pula"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl overflow-hidden" style={{ height: "180px" }}>
                <img
                  src="https://egress.storeden.net/gallery/696909ce5fb8e02d6526875d"
                  alt="Mattina in spiaggia a Is Morus"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="rounded-2xl overflow-hidden" style={{ height: "180px" }}>
                <img
                  src="https://egress.storeden.net/gallery/696909cdbe7ea033b514e443"
                  alt="Mare cristallino della Pineta Is Morus"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── Gallery ──────────────────────────────────────────────────────────────────
function GallerySection() {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState<number>(0);

  return (
    <section id="gallery" className="py-28 lg:py-36" style={{ background: "oklch(0.96 0.015 75)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="text-sm tracking-[0.25em] uppercase mb-4" style={{ color: "var(--primary)" }}>
            Gallery
          </p>
          <h2
            className="text-foreground"
            style={{ ...serif, fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, lineHeight: 1.15 }}
          >
            Life at Villa Milena
          </h2>
        </motion.div>

        {/* Main featured image + thumbnails */}
        <div className="grid lg:grid-cols-3 gap-3 mb-4">
          {/* Main */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-2 rounded-3xl overflow-hidden cursor-zoom-in"
            style={{ height: "480px" }}
            onClick={() => setLightbox(GALLERY_IMAGES[activeIdx].url)}
          >
            <img
              src={GALLERY_IMAGES[activeIdx].url}
              alt={GALLERY_IMAGES[activeIdx].alt}
              className="w-full h-full object-cover hover:scale-103 transition-transform duration-700"
            />
          </motion.div>
          {/* Thumb column */}
          <div className="flex flex-col gap-3">
            {GALLERY_IMAGES.slice(1, 5).map((img, i) => (
              <motion.div
                key={img.url}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                viewport={{ once: true }}
                className={`rounded-2xl overflow-hidden cursor-pointer flex-1 border-2 transition-all ${
                  activeIdx === i + 1 ? "border-primary" : "border-transparent hover:border-primary/40"
                }`}
                style={{ minHeight: "106px" }}
                onClick={() => setActiveIdx(i + 1)}
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom thumbnails row */}
        <div className="grid grid-cols-5 md:grid-cols-6 gap-3">
          {GALLERY_IMAGES.map((img, i) => (
            <motion.div
              key={img.url + "-thumb"}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              viewport={{ once: true }}
              className={`rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${
                activeIdx === i ? "border-primary" : "border-transparent hover:border-primary/40"
              }`}
              style={{ height: "72px" }}
              onClick={() => setActiveIdx(i)}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-8">
          <a
            href="https://vmstaysudsardegna.com/page/galleria"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            View full gallery on our website
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.93)" }}
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/70 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={lightbox}
              alt="Gallery"
              className="max-w-full max-h-full object-contain rounded-xl"
              style={{ maxHeight: "90vh" }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ── How to Book ──────────────────────────────────────────────────────────────
function HowToBookSection() {
  return (
    <section id="book" className="py-28 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-sm tracking-[0.25em] uppercase mb-4" style={{ color: "var(--primary)" }}>
            Simple Process
          </p>
          <h2
            className="text-foreground"
            style={{ ...serif, fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, lineHeight: 1.15 }}
          >
            How to Book
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 mb-14"
        >
          {HOW_TO_BOOK.map((step, i) => (
            <motion.div key={step.step} variants={fadeUp} className="text-center">
              <div className="relative flex items-center justify-center mb-8">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto"
                  style={{ background: "color-mix(in srgb, var(--primary) 12%, transparent)", color: "var(--primary)" }}
                >
                  <span className="text-2xl font-light" style={serif}>{step.step}</span>
                </div>
                {i < HOW_TO_BOOK.length - 1 && (
                  <div
                    className="hidden md:block absolute left-[calc(50%+40px)] top-1/2 -translate-y-1/2 h-px"
                    style={{ width: "calc(100% - 80px)", background: "color-mix(in srgb, var(--primary) 25%, transparent)" }}
                  />
                )}
              </div>
              <h3 className="text-foreground text-xl mb-3" style={{ ...serif, fontWeight: 600 }}>{step.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full text-white font-medium text-sm tracking-wide transition-all hover:opacity-90"
            style={{ backgroundColor: "var(--primary)" }}
          >
            <MessageCircle className="w-5 h-5" />
            Start on WhatsApp — +39 344 675 2836
          </a>
          <a
            href={`mailto:${EMAIL}`}
            className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full font-medium text-sm tracking-wide border border-border hover:border-primary/50 transition-all text-foreground"
          >
            <Mail className="w-5 h-5" />
            {EMAIL}
          </a>
        </motion.div>
      </div>
    </section>
  );
}

// ── FAQ ──────────────────────────────────────────────────────────────────────
function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section id="faq" className="py-28 lg:py-36" style={{ background: "oklch(0.96 0.015 75)" }}>
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="text-sm tracking-[0.25em] uppercase mb-4" style={{ color: "var(--primary)" }}>
            FAQ
          </p>
          <h2
            className="text-foreground"
            style={{ ...serif, fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, lineHeight: 1.15 }}
          >
            Questions & Answers
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-3"
        >
          {FAQ_ITEMS.map((item, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="bg-white rounded-2xl border border-border overflow-hidden"
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between px-7 py-5 text-left hover:bg-muted/30 transition-colors"
              >
                <span className="text-foreground font-medium text-sm pr-4">{item.q}</span>
                <ChevronDown
                  className="w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-300"
                  style={{ transform: open === i ? "rotate(180deg)" : "rotate(0deg)" }}
                />
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-7 pb-6 text-muted-foreground text-sm leading-relaxed border-t border-border/50 pt-4">
                      {item.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Final CTA ─────────────────────────────────────────────────────────────────
function FinalCTASection() {
  return (
    <section className="relative py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://egress.storeden.net/gallery/696909cc00f2201b645eb016"
          alt="Accesso privato al mare – Spiaggia Pineta Is Morus"
          className="w-full h-full object-cover"
          style={{ objectPosition: "center 55%" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(to right, rgba(10,22,32,0.88) 0%, rgba(10,22,32,0.6) 55%, rgba(10,22,32,0.35) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-white/70 text-sm tracking-[0.25em] uppercase mb-5"
          >
            Summer 2026 · Pineta Is Morus
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="text-white mb-6"
            style={{ ...serif, fontSize: "clamp(2.5rem, 5vw, 4rem)", fontWeight: 500, lineHeight: 1.1 }}
          >
            Begin your Sardinian<br />
            <em style={{ fontStyle: "italic" }}>story</em> here
          </motion.h2>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-white/80 text-lg mb-10 leading-relaxed"
          >
            Availability is limited. Contact us today to check dates and receive 
            a personalised offer for your stay at Villa Milena.
          </motion.p>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full text-white font-medium text-sm tracking-wide transition-all hover:opacity-90 hover:scale-105"
              style={{ backgroundColor: "var(--primary)", boxShadow: "0 4px 24px rgba(0,0,0,0.3)" }}
            >
              <MessageCircle className="w-5 h-5" />
              Contact us on WhatsApp
            </a>
            <a
              href={WHATSAPP_OFFER_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full text-white font-medium text-sm tracking-wide border border-white/35 hover:bg-white/10 transition-all"
            >
              Request a personalised offer
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="bg-foreground text-white/65 py-14">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-3 gap-12 mb-10">
          {/* Brand */}
          <div>
            <p className="text-white text-xl font-semibold mb-2" style={serif}>Villa Milena</p>
            <p className="text-xs tracking-[0.2em] uppercase text-white/45 mb-4">VM Stay · Sud Sardegna</p>
            <p className="text-sm leading-relaxed">
              Luxury private villa in the Pineta Is Morus reserve, Santa Margherita di Pula, 
              South Sardinia. Up to 8 guests. Weekly, long stay and tailor-made packages.
            </p>
            <p className="text-xs text-white/35 mt-4">CIN: IT092050B4000S3315</p>
          </div>

          {/* Contact */}
          <div>
            <p className="text-white text-xs font-medium mb-4 tracking-wide uppercase">Contact</p>
            <div className="space-y-3">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4" />
                +39 344 675 2836
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-2 text-sm hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                {EMAIL}
              </a>
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Loc. Santa Margherita di Pula, SS 195 km 37.88, 09050 Pula (CA), Sardegna
              </div>
            </div>
            {/* Socials */}
            <div className="flex gap-4 mt-5">
              <a
                href="https://www.facebook.com/profile.php?id=61565794236666"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm hover:text-white transition-colors"
              >
                Facebook
              </a>
              <a
                href="https://www.instagram.com/vmstaysudsardegna/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm hover:text-white transition-colors"
              >
                Instagram
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <p className="text-white text-xs font-medium mb-4 tracking-wide uppercase">Explore</p>
            <div className="space-y-2">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block text-sm hover:text-white transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <a
                href="https://vmstaysudsardegna.com/page/galleria"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm hover:text-white transition-colors"
              >
                Full Gallery
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/35">
            © 2026 VM Stay Sud Sardegna – GONZALEZ RIBALTA GLEYSI, Via Giuseppe Garibaldi n.14, P.IVA IT02757710062
          </p>
          <div className="flex items-center gap-2 text-xs text-white/35">
            <Clock className="w-3.5 h-3.5" />
            Saturday to Saturday · Min. 7 nights peak season
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <HeroSection />
        <WhySection />
        <StaysSection />
        <TailorMadeSection />
        <KosherSection />
        <LocationSection />
        <GallerySection />
        <HowToBookSection />
        <FAQSection />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
}
